/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * HyusnSu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 23 2023
 **/
import { forwardRef, ReactNode } from "react";

import { Maybe } from "@/frameworks/libraries/components";

import useTab from "./useTab";
import { useTheme } from "@emotion/react";
import { wrapperStyle, ulStyle, lineStyle, scrollWrapper } from "./private/emotion";

/**
 * @param displayType Contents 영역 처리방식 - 비활성화 contents가 normal 일때 null, hidden일때 opacity 숨김처리
 * @param uiType ui 관련 타입 ["normal" | "dot"]
 * @param underLine underline 활성화 여부
 * @param activeId 활성화된 TabId ( 첫 랜더링시에 활성화되어있어야 할 TabId )
 */
export interface TabProps extends React.HTMLAttributes<HTMLUListElement> {
	children?: ReactNode;
	displayType?: DesignStuff.Tabs.displayType;
	aspect?: "row" | "column";
	underLine?: boolean;
	lineThickness?: number;
	activeId: string;
	id?: string;
}

const Tab = forwardRef(
	(
		{ children, underLine = true, aspect = "row", id, lineThickness = 2, ...rest }: Omit<TabProps, "activeId">,
		ref: React.ForwardedRef<HTMLDivElement>
	) => {
		const { currentState, uiType } = useTab();
		const theme = useTheme();

		return (
			<div ref={ref} id={id} css={scrollWrapper}>
				<div css={wrapperStyle}>
					<ul css={[ulStyle(theme, uiType, underLine, aspect)]} {...rest}>
						{children}
					</ul>
					<Maybe test={uiType === "normal" && underLine}>
						<span css={lineStyle(theme, currentState, lineThickness)} />
					</Maybe>
				</div>
			</div>
		);
	}
);

export default Tab;
